import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [blockchainStatus, setBlockchainStatus] = useState('connected');
  const [alertCount, setAlertCount] = useState(3);
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  const moreMenuRef = useRef(null);
  const alertsRef = useRef(null);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const primaryNavItems = [
    { 
      label: 'Dashboard', 
      path: '/quality-dashboard', 
      icon: 'LayoutDashboard',
      tooltip: 'Real-time quality assurance metrics and blockchain status'
    },
    { 
      label: 'Testing', 
      path: '/batch-testing-records', 
      icon: 'FlaskConical',
      tooltip: 'Batch testing records and laboratory data entry'
    },
    { 
      label: 'Safety', 
      path: '/adverse-event-reporting', 
      icon: 'Shield',
      tooltip: 'Adverse event reporting and pharmacovigilance'
    },
    { 
      label: 'Equipment', 
      path: '/laboratory-equipment', 
      icon: 'Settings',
      tooltip: 'Laboratory equipment calibration and maintenance'
    }
  ];

  const secondaryNavItems = [
    { 
      label: 'Compliance', 
      path: '/regulatory-compliance', 
      icon: 'CheckCircle',
      tooltip: 'Regulatory compliance and submission management'
    },
    { 
      label: 'Audit', 
      path: '/audit-trail-viewer', 
      icon: 'Search',
      tooltip: 'Audit trail viewer and blockchain verification'
    }
  ];

  const mockAlerts = [
    { id: 1, type: 'warning', message: 'Equipment calibration due in 2 days', severity: 'medium' },
    { id: 2, type: 'error', message: 'Failed batch test requires review', severity: 'high' },
    { id: 3, type: 'info', message: 'Regulatory submission deadline approaching', severity: 'low' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef?.current && !moreMenuRef?.current?.contains(event?.target)) {
        setIsMoreMenuOpen(false);
      }
      if (alertsRef?.current && !alertsRef?.current?.contains(event?.target)) {
        setIsAlertsOpen(false);
      }
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMoreMenuOpen(false);
  };

  const getBlockchainStatusColor = () => {
    switch (blockchainStatus) {
      case 'connected': return 'text-success';
      case 'processing': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getAlertSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'border-l-error bg-error/5';
      case 'medium': return 'border-l-warning bg-warning/5';
      case 'low': return 'border-l-primary bg-primary/5';
      default: return 'border-l-muted bg-muted';
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-card">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo Section */}
        <div className="flex items-center space-x-8">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Blocks" size={20} color="white" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground">IFET ChainLog</span>
              <span className="text-xs text-muted-foreground font-mono">Quality Assurance</span>
            </div>
          </div>

          {/* Primary Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {primaryNavItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                  location?.pathname === item?.path
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title={item?.tooltip}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </button>
            ))}

            {/* More Menu */}
            <div className="relative" ref={moreMenuRef}>
              <button
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className="flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
              >
                <Icon name="MoreHorizontal" size={16} />
                <span>More</span>
              </button>

              {isMoreMenuOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-popover border border-border rounded-md shadow-modal animate-slide-down">
                  <div className="py-1">
                    {secondaryNavItems?.map((item) => (
                      <button
                        key={item?.path}
                        onClick={() => handleNavigation(item?.path)}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm transition-colors duration-150 ${
                          location?.pathname === item?.path
                            ? 'bg-accent text-accent-foreground'
                            : 'text-popover-foreground hover:bg-muted'
                        }`}
                        title={item?.tooltip}
                      >
                        <Icon name={item?.icon} size={16} />
                        <span>{item?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          {/* Blockchain Status */}
          <div className="hidden md:flex items-center space-x-2 px-3 py-1 bg-muted rounded-full">
            <div className={`w-2 h-2 rounded-full ${getBlockchainStatusColor()}`}>
              <div className="w-full h-full bg-current rounded-full animate-pulse"></div>
            </div>
            <span className="text-xs font-mono text-muted-foreground capitalize">
              {blockchainStatus}
            </span>
          </div>

          {/* Critical Alerts */}
          <div className="relative" ref={alertsRef}>
            <button
              onClick={() => setIsAlertsOpen(!isAlertsOpen)}
              className="relative p-2 text-muted-foreground hover:text-foreground transition-colors duration-150"
            >
              <Icon name="Bell" size={20} />
              {alertCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center font-medium">
                  {alertCount}
                </span>
              )}
            </button>

            {isAlertsOpen && (
              <div className="absolute top-full right-0 mt-1 w-80 bg-popover border border-border rounded-md shadow-modal animate-slide-down">
                <div className="p-4 border-b border-border">
                  <h3 className="font-medium text-popover-foreground">Critical Alerts</h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {mockAlerts?.map((alert) => (
                    <div
                      key={alert?.id}
                      className={`p-4 border-l-4 ${getAlertSeverityColor(alert?.severity)} hover:bg-muted/50 transition-colors duration-150`}
                    >
                      <p className="text-sm text-popover-foreground">{alert?.message}</p>
                      <p className="text-xs text-muted-foreground mt-1 capitalize">
                        {alert?.severity} priority
                      </p>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-border">
                  <Button variant="outline" size="sm" className="w-full">
                    View All Alerts
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* User Context */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted transition-colors duration-150"
            >
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="white" />
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-foreground">Dr. Sarah Chen</p>
                <p className="text-xs text-muted-foreground">QA Manager</p>
              </div>
              <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
            </button>

            {isUserMenuOpen && (
              <div className="absolute top-full right-0 mt-1 w-64 bg-popover border border-border rounded-md shadow-modal animate-slide-down">
                <div className="p-4 border-b border-border">
                  <p className="font-medium text-popover-foreground">Dr. Sarah Chen</p>
                  <p className="text-sm text-muted-foreground">Quality Assurance Manager</p>
                  <p className="text-xs text-muted-foreground mt-1">Lab Assignment: Main Facility</p>
                </div>
                <div className="py-1">
                  <button className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors duration-150">
                    <Icon name="Settings" size={16} />
                    <span>Settings</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors duration-150">
                    <Icon name="HelpCircle" size={16} />
                    <span>Help & Support</span>
                  </button>
                  <div className="border-t border-border my-1"></div>
                  <button className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-error hover:bg-error/5 transition-colors duration-150">
                    <Icon name="LogOut" size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden p-2 text-muted-foreground hover:text-foreground transition-colors duration-150">
            <Icon name="Menu" size={20} />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;